import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Pane, Button, MenuSection, ConfirmationModal, ErrorModal, } from "@folio/stripes/components";
import { useIntl, FormattedMessage } from "react-intl";
import NavComponent from "../../components/NavComponent";
import TableComponent from "../../components/TableComponent";
import { LibraryDetail } from "../../components/library/LibraryDetail";
import { LibraryEmailTemplatesDialog } from "../../components/library/LibraryEmailTemplatesDialog";
import { RecekPaneSet } from "../../components/RecekPaneSet";
import { createStripesSource } from "../../utils/createStripesSource";
import { formatCountSubtitle } from "../../utils/paneCount";
import { useLibraryActions } from "../../hooks/useLibraryActions";
import { useLibraryTable } from "../../hooks/useLibraryTable";
import { useLibraryFilters } from "../../hooks/useLibraryFilters";
import { LibraryFilters } from "../../components/LibraryFilters";
import { useLibrariesData } from "../../hooks/useLibrariesData";
// shared table sort hook; its sessionStorage prefix is still pvTitleSort. so the
// existing prefix cleanup in ui-br-stripes-core keeps covering this key too
import { useTitleSort } from "../../hooks/useTitleSort";
import { useListErrorMessage } from "../../hooks/useListErrorMessage";
import { columnSortKeys } from "../../utils/titleSort";
import { usePermissions } from "../../hooks/usePermissions";
import { useLibraryDeleteMutation } from "../../hooks/useLibraryDeleteMutation";
import { useLibraryDeletePreview } from "../../hooks/useLibraryDeletePreview";
import { libraryEdit, ROUTE_PATHS } from "../../types/routePaths";
import { useModal } from "../../utils/useModal";
import { useEditNavigation } from "../../utils/listReturn";
import { useMessageTemplatesDialogParam } from "../../hooks/useMessageTemplatesDialogParam";
var LibrariesList = function () {
    var _a, _b, _c, _d;
    var history = useHistory();
    var location = useLocation();
    var navigateToEdit = useEditNavigation();
    var formatMessage = useIntl().formatMessage;
    var _e = useState(true), filterPaneIsVisible = _e[0], setFilterPaneIsVisible = _e[1];
    var _f = useState(false), showDeleteConfirm = _f[0], setShowDeleteConfirm = _f[1];
    var _g = useState(null), preview = _g[0], setPreview = _g[1];
    var _h = useModal(), isErrorModalOpen = _h.isOpen, errorModalMessage = _h.message, showErrorModal = _h.openModal, hideErrorModal = _h.closeModal;
    var filtersHook = useLibraryFilters();
    var emailTemplates = useMessageTemplatesDialogParam();
    var _j = usePermissions(), canCreateLibraryItem = _j.canCreateLibraryItem, canEditLibraryItem = _j.canEditLibraryItem;
    var actionsHook = useLibraryActions();
    var emailTemplatesType = emailTemplates.selectedType, closeEmailTemplates = emailTemplates.closeDialog;
    var hasDetailParam = new URLSearchParams(location.search).has("detail");
    useEffect(function () {
        // a param left behind by a closed detail would reopen the editor for the next clicked library
        if (emailTemplatesType !== null && !hasDetailParam) {
            closeEmailTemplates();
        }
    }, [emailTemplatesType, hasDetailParam, closeEmailTemplates]);
    var columns = useLibraryTable(actionsHook.handleRowClick).columns;
    var sortKeys = useMemo(function () { return columnSortKeys(columns); }, [columns]);
    var _k = useTitleSort("libraries", sortKeys), sort = _k.sort, handleSort = _k.handleSort;
    var dataHook = useLibrariesData(filtersHook.appliedFilters, sort);
    var errorMessage = useListErrorMessage(dataHook.error);
    var handleSortAndRestartPaging = useCallback(function (sortKey) {
        dataHook.fetchMoreData(0);
        handleSort(sortKey);
    }, [dataHook, handleSort]);
    var toggleFilterPane = useCallback(function () { return setFilterPaneIsVisible(function (prev) { return !prev; }); }, []);
    var source = createStripesSource({
        ky: null,
        data: dataHook.librariesData,
        totalRecords: dataHook.totalRecords,
        isLoading: dataHook.isLoading,
        isError: dataHook.isError,
        errorMessage: errorMessage,
        isLoaded: true,
        fetchMore: function () {
            return dataHook.fetchMoreData(dataHook.pageOffset + dataHook.pageSize);
        },
    });
    var previewMutation = useLibraryDeletePreview(useCallback(function (loaded) {
        setPreview(loaded);
        setShowDeleteConfirm(true);
    }, []), useCallback(function () {
        showErrorModal(formatMessage({ id: "ui-br-legaldeposits.libraries.delete.error" }));
    }, [formatMessage, showErrorModal]));
    var deleteMutation = useLibraryDeleteMutation(useCallback(function () {
        setShowDeleteConfirm(false);
        setPreview(null);
        actionsHook.handleCloseDetail();
    }, [actionsHook]), useCallback(function () {
        showErrorModal(formatMessage({ id: "ui-br-legaldeposits.libraries.delete.error" }));
    }, [formatMessage, showErrorModal]));
    useEffect(function () {
        if (!showDeleteConfirm) {
            setPreview(null);
        }
    }, [showDeleteConfirm]);
    var handleDeleteClick = useCallback(function () {
        var _a;
        if ((_a = actionsHook.selectedLibrary) === null || _a === void 0 ? void 0 : _a.id) {
            previewMutation.mutate(actionsHook.selectedLibrary.id);
        }
    }, [actionsHook.selectedLibrary, previewMutation]);
    var handleConfirmDelete = useCallback(function () {
        var _a;
        if (deleteMutation.isLoading)
            return;
        if ((_a = actionsHook.selectedLibrary) === null || _a === void 0 ? void 0 : _a.id) {
            deleteMutation.mutate(actionsHook.selectedLibrary.id);
        }
    }, [actionsHook.selectedLibrary, deleteMutation]);
    var handleCancelDelete = useCallback(function () {
        setShowDeleteConfirm(false);
    }, []);
    return (React.createElement(RecekPaneSet, null,
        React.createElement(ErrorModal, { open: isErrorModalOpen, label: formatMessage({ id: "ui-br-legaldeposits.modal.error.label" }), content: errorModalMessage, onClose: hideErrorModal }),
        filterPaneIsVisible && (React.createElement(Pane, { id: "menuPane", defaultWidth: "25%", paneTitle: formatMessage({
                id: "ui-br-legaldeposits.pane.title.navigation",
            }) },
            React.createElement("div", { style: { display: "flex", flexDirection: "column" } },
                React.createElement(NavComponent, null),
                React.createElement(Button, { buttonStyle: "none", id: "clickable-reset-all", disabled: !filtersHook.isAnyFilterActive(), marginBottom0: true, onClick: filtersHook.handleClearFilters },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.button.resetAll" })),
                React.createElement(LibraryFilters, { filters: filtersHook.filters, onFilterChange: filtersHook.handleFilterChange, onTypeChange: filtersHook.handleTypeChange, onApplyFilters: filtersHook.handleApplyFilters, hasUnappliedChanges: filtersHook.hasUnappliedChanges() })))),
        React.createElement(Pane, { id: "contentPane", defaultWidth: "fill", paneTitle: formatMessage({
                id: "ui-br-legaldeposits.navigation.libraries",
            }), paneSub: formatCountSubtitle(formatMessage, dataHook.totalRecords, dataHook.isCountCapped), actionMenu: function (_a) {
                var onToggle = _a.onToggle;
                return (React.createElement(MenuSection, { label: formatMessage({
                        id: "ui-br-legaldeposits.action.menu.label",
                    }), id: "actions-menu" }, canCreateLibraryItem && (React.createElement(Button, { buttonStyle: "dropdownItem", onClick: function () {
                        onToggle();
                        history.push("".concat(ROUTE_PATHS.LIBRARIES, "/create"));
                    } },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.button.create" })))));
            } },
            React.createElement(TableComponent, { columnPrefsKey: "libraries", data: dataHook.librariesData, columns: columns, pageSize: dataHook.pageSize, onPageSizeChange: dataHook.changePageSize, totalCount: dataHook.totalRecords, onNeedMoreData: function (offset) { return dataHook.fetchMoreData(offset); }, searchTerm: "", filterPaneIsVisible: filterPaneIsVisible, toggleFilterPane: toggleFilterPane, source: source, isFetching: dataHook.isFetching, offset: dataHook.pageOffset, onRowClick: actionsHook.handleRowClick, sortBy: sort.sortBy, sortDir: sort.sortDir, onSort: handleSortAndRestartPaging, resetPaginationKey: "".concat(JSON.stringify(filtersHook.appliedFilters), "|").concat(sort.sortBy, "|").concat(sort.sortDir) })),
        actionsHook.selectedLibrary && (React.createElement(Pane, { id: "detailPane", defaultWidth: "35%", paneTitle: formatMessage({
                id: "ui-br-legaldeposits.libraries.detail.pane.title",
            }), onClose: actionsHook.handleCloseDetail, dismissible: true, actionMenu: function (_a) {
                var onToggle = _a.onToggle;
                return (React.createElement(MenuSection, { label: formatMessage({
                        id: "ui-br-legaldeposits.action.menu.label",
                    }), id: "actions-menu" },
                    canEditLibraryItem && (React.createElement(Button, { buttonStyle: "dropdownItem", onClick: function () {
                            onToggle();
                            navigateToEdit(libraryEdit(actionsHook.selectedLibrary.id));
                        }, fullWidth: true },
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.libraries.detail.action.edit" }))),
                    canEditLibraryItem && (React.createElement(Button, { buttonStyle: "dropdownItem", onClick: function () {
                            onToggle();
                            emailTemplates.openDialog();
                        }, fullWidth: true },
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.libraries.detail.action.editMessageTemplates" }))),
                    canEditLibraryItem &&
                        actionsHook.selectedLibrary.status === "ACTIVE" && (React.createElement(Button, { buttonStyle: "dropdownItem", onClick: function () {
                            onToggle();
                            handleDeleteClick();
                        }, fullWidth: true, disabled: previewMutation.isLoading },
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.libraries.detail.action.delete" })))));
            } },
            React.createElement(LibraryDetail, { library: actionsHook.selectedLibrary }))),
        React.createElement(ConfirmationModal, { open: showDeleteConfirm, heading: formatMessage({
                id: "ui-br-legaldeposits.libraries.delete.confirm.heading",
            }), message: React.createElement(React.Fragment, null,
                React.createElement("p", null,
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.libraries.delete.confirm.message" })),
                preview &&
                    preview.affectedUsers &&
                    preview.affectedUsers.length > 0 ? (React.createElement(React.Fragment, null,
                    React.createElement("p", null,
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.libraries.delete.confirm.affectedUsers" })),
                    React.createElement("ul", null, preview.affectedUsers.map(function (affectedUser) { return (React.createElement("li", { key: affectedUser.id },
                        affectedUser.firstName,
                        " ",
                        affectedUser.lastName,
                        " (",
                        affectedUser.email,
                        ")")); })))) : (React.createElement("p", null,
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.libraries.delete.confirm.noUsers" })))), confirmLabel: formatMessage({
                id: "ui-br-legaldeposits.libraries.delete.confirm.button",
            }), onConfirm: handleConfirmDelete, onCancel: handleCancelDelete }),
        React.createElement(LibraryEmailTemplatesDialog, { open: canEditLibraryItem &&
                emailTemplates.selectedType !== null &&
                !!actionsHook.selectedLibrary, onClose: emailTemplates.closeDialog, libraryId: (_b = (_a = actionsHook.selectedLibrary) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null, libraryName: (_c = actionsHook.selectedLibrary) === null || _c === void 0 ? void 0 : _c.name, selectedType: (_d = emailTemplates.selectedType) !== null && _d !== void 0 ? _d : "URGENCE", onSelectedTypeChange: emailTemplates.setSelectedType })));
};
export default LibrariesList;
